import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, IconButton, Popover, TextField, Tooltip, Flex, Text } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { ArrowRight, CircleHelp, Home, QrCode, Search, Settings, Sparkles, Wifi } from "lucide-react";
import { BoltIcon, ClipboardDocumentListIcon, StarIcon, MegaphoneIcon } from "@heroicons/react/16/solid";
import { SEARCHABLE_ROUTES } from "../../routeConfig";
import { useProfileContext } from "context";
import "./HomeToolbar.css";

const SEARCH_DESTINATIONS = SEARCHABLE_ROUTES.map((route) => ({ ...route, to: route.path }));

const PAGE_SECTION_RULES = [
  { prefixes: ["/ordering"], title: "QR Ordering", icon: QrCode, name: "qr-code" },
  { prefixes: ["/ai-assistant"], title: "AI Assistant", icon: Sparkles, name: "sparkles" },
  { prefixes: ["/guest-wifi", "/presence", "/portal-", "/networks"], title: "Guest WiFi", icon: Wifi, name: "wifi" },
  { prefixes: ["/marketing"], title: "Marketing", icon: MegaphoneIcon, name: "MegaphoneIcon" },
  { prefixes: ["/surveys"], title: "Surveys", icon: ClipboardDocumentListIcon, name: "clipboard-list" },
  { prefixes: ["/reviews"], title: "Reviews", icon: StarIcon, name: "star" },
  { prefixes: ["/integrations"], title: "Integrations", icon: BoltIcon, name: "plug-zap" },
  { prefixes: ["/organisation", "/profile"], title: "Settings", icon: Settings, name: "settings" },
];

const getPageSection = (pathname) => PAGE_SECTION_RULES.find(
  ({ prefixes }) => prefixes.some((prefix) => pathname.startsWith(prefix))
) || { title: "Home", icon: Home, name: "home" };

const isOrderedMatch = (query, value) => {
  let queryIndex = 0;
  for (const character of value) {
    if (character === query[queryIndex]) queryIndex += 1;
    if (queryIndex === query.length) return true;
  }
  return false;
};

export default function HomeToolbar() {
  const { userProfile } = useProfileContext();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ K" : "Ctrl K";
  const {
    title: currentSection,
    icon: CurrentPageIcon,
    name: currentPageIconName,
  } = getPageSection(location.pathname);

  const searchResults = useMemo(() => {
    const words = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const destinations = SEARCH_DESTINATIONS.filter((route) => !route.ownerOnly || userProfile?.user_role === "Owner");
    if (words.length === 0) return destinations;

    return destinations.map((destination) => {
      const title = destination.title.toLowerCase();
      const titleWords = title.split(" ").filter(Boolean);
      const description = destination.description.toLowerCase();
      const keywords = destination.keywords.toLowerCase();
      const searchable = `${destination.title} ${destination.description} ${destination.keywords}`.toLowerCase();
      const matches = words.every((word) => (
        word.length <= 2
          ? titleWords.some((titleWord) => isOrderedMatch(word, titleWord))
          : searchable.includes(word)
      ));
      if (!matches) return null;

      const phrase = words.join(" ");
      let score = 0;
      if (title.startsWith(phrase)) score += 100;
      if (words.every((word) => titleWords.some((titleWord) => titleWord.startsWith(word)))) score += 70;
      if (words.every((word) => titleWords.some((titleWord) => isOrderedMatch(word, titleWord)))) score += 40;
      if (title.includes(phrase)) score += 50;
      if (description.includes(phrase)) score += 20;
      if (keywords.includes(phrase)) score += 10;
      return { destination, score };
    })
      .filter(Boolean)
      .sort((left, right) => right.score - left.score)
      .map(({ destination }) => destination);
  }, [searchQuery, userProfile?.user_role]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
        setSearchQuery("");
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const handleSearchOpenChange = (open) => {
    setSearchOpen(open);
    if (!open) setSearchQuery("");
  };

  return (
    <Flex px="5" className="home-toolbar">
      <Flex className="home-toolbar-leading" align="center">
        <Flex gap="2" direction="row" align="center">
          <CurrentPageIcon width={18} height={18} aria-hidden="true" data-testid="current-page-icon" data-icon={currentPageIconName} />
          <Text weight="medium" size="3" color="gray">{currentSection}</Text>
          <Tooltip side="right"
            content={`${currentSection === "Marketing" 
              ? "Manage and analyse your marketing campaigns across multiple channels and leverage data-driven insights and automation to boost engagement and drive growth." 
              : currentSection === "Reviews" 
              ? "Manage and analyse your customer reviews and feedback across multiple platforms to improve your business reputation and customer satisfaction."
              : currentSection === "Guest WiFi"
              ? "Monitor guest Wi-Fi performance, network health, and access point status across all locations. Easily configure Guest, Work, and Staff networks, customise your captive portal, and analyse contact data."
              : currentSection === "Surveys"
              ? "Create and manage customer feedback surveys with customisable form blocks to gather insights from your visitors."
              : currentSection === "QR Ordering"
              ? "Manage restaurant orders, menu items, tables, QR codes and guest menu settings."
              : currentSection === "Integrations"
              ? "Connect and manage integration settings to enhance your platform's capabilities."
              : currentSection === "AI Assistant"
              ? "Manage your organisation’s central AI Assistant and its public identifier."
              : currentSection === "Settings"
              ? "Manage your organisational level settings, subscriptions and other details."
              : ""} `}
          >
            <InfoCircledIcon width={16} height={16} color="gray"/>
          </Tooltip>
        </Flex>
        <Popover.Root open={searchOpen} onOpenChange={handleSearchOpenChange}>
          <Popover.Trigger>
            <button type="button" className="home-toolbar-search" aria-label="Search b connect" 
              aria-keyshortcuts="Control+k Meta+k">
              <Search size={17} aria-hidden="true" />
              <span>Search pages and tools...</span>
              <kbd aria-hidden="true">{shortcut}</kbd>
            </button>
          </Popover.Trigger>
          <Popover.Content
            className="home-search-dropdown"
            width="560px"
            align="start"
            sideOffset={8}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              searchInputRef.current?.focus();
            }}
          >
            <TextField.Root
              ref={searchInputRef}
              size="3"
              placeholder="Try reviews, campaigns or WiFi"
              aria-label="Search pages and tools"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && searchResults.length > 0) {
                  event.preventDefault();
                  navigate(searchResults[0].to);
                  handleSearchOpenChange(false);
                }
              }}
            >
              <TextField.Slot><Search size={17} aria-hidden="true" /></TextField.Slot>
            </TextField.Root>
            <div className="home-search-results" aria-label="Search results">
              {searchResults.length === 0 ? (
                <p className="home-toolbar-empty" role="status">No pages found. Try a different search.</p>
              ) : (
                <ul>
                  {searchResults.map((result) => (
                    <li key={result.to}>
                      <Link to={result.to} className="home-search-result" onClick={() => handleSearchOpenChange(false)}>
                        <span><strong>{result.title}</strong><small>{result.description}</small></span>
                        <ArrowRight size={16} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="home-search-hint">Press Enter to open the first result · Esc to close</p>
          </Popover.Content>
        </Popover.Root>
      </Flex>

      <Flex className="home-toolbar-actions">
        <Tooltip content="Email support">
          <IconButton asChild variant="ghost" color="gray" size="3" className="home-toolbar-icon">
            <a href="mailto:support@b-connect.co.uk" aria-label="Email support">
                <CircleHelp size={20} strokeWidth={1.8} aria-hidden="true" />
            </a>
          </IconButton>
        </Tooltip>

        <Button size="2" className="home-toolbar-ai" disabled aria-label="AI assistance, coming soon">
          <Sparkles size={16} aria-hidden="true" /><span>AI assistance</span><small>Coming soon</small>
        </Button>
      </Flex>
    </Flex>
  );
}
